import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Upload, ArrowLeftRight, TrendingUp, Coins, Zap } from 'lucide-react';
import { totalPortfolioValue, mainAccountValue, futuresAccountValue, spotBalance, marginBalance, futuresBalance, earnBalance, spotUpnl, marginPositionsUpnl, futuresUnrealizedPnl, earnRewards } from '../../utils/portfolioData';
import { AllocationChart } from '../home/AllocationChart';
export function PortfolioAccountOverview() {
  const navigate = useNavigate();
  const accounts = [{
    id: 'main',
    name: 'Main Account',
    icon: <Coins size={20} data-id="element-1133" />,
    totalValue: mainAccountValue,
    subAccounts: [{
      name: 'Spot',
      balance: spotBalance,
      upnl: spotUpnl
    }, {
      name: 'Margin',
      balance: marginBalance,
      upnl: marginPositionsUpnl
    }],
    color: 'bg-blue-500/10 text-blue-400'
  }, {
    id: 'futures',
    name: 'Futures',
    icon: <TrendingUp size={20} data-id="element-1134" />,
    totalValue: futuresAccountValue,
    balance: futuresBalance,
    upnl: futuresUnrealizedPnl,
    color: 'bg-purple-500/10 text-purple-400'
  }, {
    id: 'earn',
    name: 'Earn',
    icon: <Zap size={20} data-id="element-1135" />,
    totalValue: earnBalance,
    balance: earnBalance,
    rewards: earnRewards,
    color: 'bg-green-500/10 text-green-400'
  }];
  const totalChange = spotUpnl + marginPositionsUpnl + futuresUnrealizedPnl;
  const totalChangePercent = totalChange / totalPortfolioValue * 100;
  const handleAccountClick = (accountId: string) => {
    // Update URL to show the account (you can use query params or state)
    window.location.hash = accountId;
    // Trigger a re-render or navigation if needed
  };
  return <div className="space-y-6" data-id="element-1136">
      {/* Total Portfolio Value Card with Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6" data-id="element-1137">
        <div className="bg-background-secondary border border-border-subtle rounded-[20px] p-6" data-id="element-1138">
          <div className="flex items-start justify-between mb-6" data-id="element-1139">
            <div data-id="element-1140">
              <div className="text-sm text-text-secondary mb-1 font-label uppercase" data-id="element-1141">
                Total Portfolio Value
              </div>
              <div className="flex items-baseline gap-2 mb-2" data-id="element-1142">
                <h1 className="text-5xl font-light text-text-primary tracking-tight font-numbers" data-id="element-1143">
                  {totalPortfolioValue.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                </h1>
                <span className="text-xl text-text-secondary font-normal" data-id="element-1144">
                  USD
                </span>
              </div>
              <span className={`flex items-center gap-1 text-sm font-medium ${totalChange >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1145">
                <TrendingUp size={14} className={totalChange < 0 ? 'rotate-180' : ''} data-id="element-1146" />
                {totalChange >= 0 ? '+' : ''}$
                {Math.abs(totalChange).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}{' '}
                ({totalChange >= 0 ? '+' : ''}
                {totalChangePercent.toFixed(2)}%)
              </span>
            </div>

            <div className="flex items-center gap-2" data-id="element-1147">
              <button className="flex items-center gap-2 px-4 py-2 text-sm text-text-secondary hover:text-text-primary border border-border-subtle hover:border-border-default rounded-full transition-colors" data-id="element-1148">
                <ArrowLeftRight size={16} data-id="element-1149" />
                Transfer
              </button>
              <button className="flex items-center gap-2 px-4 py-2 text-sm text-text-secondary hover:text-text-primary border border-border-subtle hover:border-border-default rounded-full transition-colors" data-id="element-1150">
                <Upload size={16} data-id="element-1151" />
                Withdraw
              </button>
              <button className="flex items-center gap-2 px-4 py-2 text-sm text-text-secondary hover:text-text-primary border border-border-subtle hover:border-border-default rounded-full transition-colors" data-id="element-1152">
                <Download size={16} data-id="element-1153" />
                Deposit
              </button>
            </div>
          </div>
        </div>

        {/* Total Holdings Chart */}
        <div className="bg-background-secondary border border-border-subtle rounded-[20px] p-6" data-id="element-1154">
          <AllocationChart data-id="element-1155" />
        </div>
      </div>

      {/* Account Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1156">
        {accounts.map(account => <button key={account.id} onClick={() => handleAccountClick(account.id)} className="bg-background-secondary border border-border-subtle rounded-[20px] p-6 hover:border-border-default transition-all cursor-pointer text-left" data-id="element-1157">
            <div className="flex items-start justify-between mb-4" data-id="element-1158">
              <div className="flex items-center gap-3" data-id="element-1159">
                <div className={`p-2 rounded-lg ${account.color}`} data-id="element-1160">
                  {account.icon}
                </div>
                <div data-id="element-1161">
                  <h3 className="text-lg font-semibold text-text-primary" data-id="element-1162">
                    {account.name}
                  </h3>
                  {account.subAccounts && <p className="text-xs text-text-secondary" data-id="element-1163">
                      {account.subAccounts.length} sub-accounts
                    </p>}
                </div>
              </div>
            </div>

            <div className="space-y-3" data-id="element-1164">
              <div data-id="element-1165">
                <div className="text-xs text-text-secondary mb-1" data-id="element-1166">
                  Total Value
                </div>
                <div className="text-2xl font-numbers text-text-primary" data-id="element-1167">
                  {account.totalValue.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}{' '}
                  <span className="text-sm text-text-secondary" data-id="element-1168">USD</span>
                </div>
              </div>

              {account.subAccounts ? <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border-subtle" data-id="element-1169">
                  {account.subAccounts.map(sub => <div key={sub.name} data-id="element-1170">
                      <div className="text-xs text-text-secondary mb-1" data-id="element-1171">
                        {sub.name}
                      </div>
                      <div className="text-sm font-numbers text-text-primary" data-id="element-1172">
                        {sub.balance.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                      </div>
                      <div className={`text-xs font-numbers ${sub.upnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1173">
                        {sub.upnl >= 0 ? '+' : ''}
                        {sub.upnl.toFixed(2)}
                      </div>
                    </div>)}
                </div> : <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border-subtle" data-id="element-1174">
                  {account.balance !== undefined && <div data-id="element-1175">
                      <div className="text-xs text-text-secondary mb-1" data-id="element-1176">
                        Balance
                      </div>
                      <div className="text-sm font-numbers text-text-primary" data-id="element-1177">
                        {account.balance.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                      </div>
                    </div>}
                  {account.upnl !== undefined && <div data-id="element-1178">
                      <div className="text-xs text-text-secondary mb-1" data-id="element-1179">
                        UP&L
                      </div>
                      <div className={`text-sm font-numbers ${account.upnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1180">
                        {account.upnl >= 0 ? '+' : ''}
                        {account.upnl.toFixed(2)}
                      </div>
                    </div>}
                  {account.rewards !== undefined && <div data-id="element-1181">
                      <div className="text-xs text-text-secondary mb-1" data-id="element-1182">
                        Rewards (1w)
                      </div>
                      <div className="text-sm font-numbers text-success" data-id="element-1183">
                        +{account.rewards.toFixed(2)}
                      </div>
                    </div>}
                </div>}
            </div>
          </button>)}
      </div>
    </div>;
}